/**
 * Contact Page Component
 * Enhanced version with contact cards and mailto form
 *
 * Features:
 * - Contact form with mailto (no Firebase required)
 * - Contact method cards (WhatsApp, Phone, Email, Facebook)
 * - Animated interactions
 * - Full i18n support
 * - Accessible design
 * - SEO optimized
 */

import { useState } from "react";
import {
  Typography,
  Box,
  Container,
  TextField,
  Button,
  Grid,
  Paper,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Link as MuiLink,
} from "@mui/material";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Contact methods configuration
  const contactMethods = [
    {
      icon: <WhatsAppIcon sx={{ fontSize: 40 }} />,
      title: "WhatsApp",
      content: "(661) 130 1597",
      href: "https://wa.me/526611301597?text=" + encodeURIComponent(t("whatsapp.defaultMessage")),
      action: t("contact.methods.whatsappAction"),
      color: "#25D366",
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 40 }} />,
      title: t("contact.methods.phone"),
      content: "(661) 130 1597",
      href: "tel:+526611301597",
      action: t("contact.methods.phoneAction"),
      color: "#1976d2",
    },
    {
      icon: <EmailIcon sx={{ fontSize: 40 }} />,
      title: t("contact.methods.email"),
      content: "itenginesparks@gmail.com",
      href: "mailto:itenginesparks@gmail.com",
      action: t("contact.methods.emailAction"),
      color: "#EA4335",
    },
    {
      icon: <FacebookIcon sx={{ fontSize: 40 }} />,
      title: "Facebook",
      content: "IT Engine Sparks",
      href: "https://www.facebook.com/profile.php?id=61569313564630",
      action: t("contact.methods.facebookAction"),
      color: "#1877F2",
    },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Build mailto URL with form data
    const subject = encodeURIComponent(
      `${t("contact.form.emailSubject")} - ${formData.name}`
    );
    const body = encodeURIComponent(
      `${t("contact.form.emailBody")}\n\n` +
      `${t("contact.form.name")}: ${formData.name}\n` +
      `${t("contact.form.email")}: ${formData.email}\n\n` +
      `${t("contact.form.message")}:\n${formData.message}`
    );

    // Open default email client
    window.location.href = `mailto:itenginesparks@gmail.com?subject=${subject}&body=${body}`;

    // Show success message
    setSnackbarMessage(t("contact.mailtoSuccess"));
    setOpenSnackbar(true);

    // Clear form
    setFormData({ name: "", email: "", message: "" });
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box>
      <Helmet>
        <title>{t("contact.title")}</title>
        <meta name="description" content={t("contact.description")} />
        <meta property="og:title" content={t("contact.ogTitle")} />
        <meta property="og:description" content={t("contact.ogDescription")} />
        <meta property="og:image" content="https://itenginesparks.com/og-banner.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="IT Engine Sparks — Web & Software Development" />
        <meta property="og:url" content="https://itenginesparks.com/contact" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://itenginesparks.com/contact" />
        <meta name="twitter:title" content={t("contact.ogTitle")} />
        <meta name="twitter:description" content={t("contact.description")} />
        <meta name="twitter:image" content="https://itenginesparks.com/og-banner.png" />
      </Helmet>

      {/* Hero Header */}
      <Box
        sx={{
          position: "relative",
          color: "white",
          py: 10,
          textAlign: "center",
          overflow: "hidden",
          backgroundImage: "url('/images/uiux.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "linear-gradient(135deg, rgba(8,18,38,0.88) 0%, rgba(21,101,192,0.75) 60%, rgba(180,70,0,0.50) 100%)",
            zIndex: 0,
          },
        }}
      >
        <Container sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              className="animated-gradient-text"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              {t("contact.heading")}
            </Typography>
            <Typography
              variant="h6"
              sx={{ maxWidth: 800, mx: "auto", opacity: 0.95 }}
            >
              {t("contact.subheading")}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Main Content */}
      <Container sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: "100%",
                  backdropFilter: "blur(10px)",
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(30, 30, 30, 0.8)"
                      : "rgba(255, 255, 255, 0.8)",
                }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  {t("contact.form.title")}
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <motion.div whileFocus={{ scale: 1.01 }}>
                        <TextField
                          fullWidth
                          label={t("contact.form.name")}
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </motion.div>
                    </Grid>

                    <Grid item xs={12}>
                      <motion.div whileFocus={{ scale: 1.01 }}>
                        <TextField
                          fullWidth
                          label={t("contact.form.email")}
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </motion.div>
                    </Grid>

                    <Grid item xs={12}>
                      <motion.div whileFocus={{ scale: 1.01 }}>
                        <TextField
                          fullWidth
                          label={t("contact.form.message")}
                          name="message"
                          multiline
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </motion.div>
                    </Grid>

                    <Grid item xs={12}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          size="large"
                          fullWidth
                          sx={{
                            py: 1.5,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                          }}
                        >
                          {t("contact.form.send")}
                        </Button>
                      </motion.div>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </motion.div>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                {t("contact.contactInfo")}
              </Typography>

              <Grid container spacing={2}>
                {contactMethods.map((method, index) => (
                  <Grid item xs={12} sm={6} key={method.title}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                    >
                      <Card
                        sx={{
                          height: "100%",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: `0 12px 24px ${method.color}33`,
                          },
                        }}
                      >
                        <CardContent sx={{ textAlign: "center", p: 3 }}>
                          <Box
                            sx={{
                              width: 70,
                              height: 70,
                              borderRadius: "50%",
                              bgcolor: method.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mx: "auto",
                              mb: 2,
                              color: "white",
                              boxShadow: `0 4px 12px ${method.color}44`,
                            }}
                          >
                            {method.icon}
                          </Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                          >
                            {method.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2, wordBreak: "break-word" }}
                          >
                            {method.content}
                          </Typography>
                          <MuiLink
                            href={method.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{
                              color: method.color,
                              fontWeight: 600,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              transition: "all 0.2s",
                              "&:hover": {
                                color: method.color,
                                opacity: 0.8,
                                transform: "translateX(4px)",
                              },
                            }}
                          >
                            {method.action} →
                          </MuiLink>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>

              {/* Additional Social Links */}
              <Box sx={{ mt: 4 }}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t("footer.followUs")}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
                    {[
                      { href: "https://wa.me/526611301597", icon: <WhatsAppIcon sx={{ fontSize: 40 }} />, color: "#25D366", label: "WhatsApp" },
                      { href: "https://www.facebook.com/profile.php?id=61569313564630", icon: <FacebookIcon sx={{ fontSize: 40 }} />, color: "#1877F2", label: "Facebook" },
                      { href: "https://www.linkedin.com/in/carlosavilez/", icon: <LinkedInIcon sx={{ fontSize: 40 }} />, color: "#0A66C2", label: "LinkedIn" },
                      { href: "https://instagram.com/itenginesparks", icon: <InstagramIcon sx={{ fontSize: 40 }} />, color: "#E4405F", label: "Instagram" },
                    ].map(({ href, icon, color, label }) => (
                      <motion.div key={label} whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
                        <MuiLink
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          sx={{
                            color,
                            display: "inline-flex",
                            borderRadius: "50%",
                            p: 0.5,
                            transition: "box-shadow 0.25s",
                            "&:hover": { boxShadow: `0 0 16px 5px ${color}66` },
                          }}
                        >
                          {icon}
                        </MuiLink>
                      </motion.div>
                    ))}
                  </Box>
                </Paper>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Snackbar for success message */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </motion.div>
      </Snackbar>
    </Box>
  );
}
